/**
 * AASHTO 1993 Constants Service
 *
 * Centralizes all AASHTO equation constants to enable dynamic configuration
 * and maintain consistency across the application.
 *
 * Based on AASHTO Guide for Design of Pavement Structures 1993
 */

export interface IAASHTOEquationConstants {
    // Structural Number Factor (SN term)
    SN_COEFFICIENT: number; // 9.36
    SN_CONSTANT: number; // -0.2
    SN_EXPONENT: number; // 5.19

    // Serviceability Factor (Delta PSI term)
    SERVICEABILITY_INTERCEPT: number; // 4.2
    SERVICEABILITY_SLOPE: number; // 1.5 (4.2 - 1.5 = 2.7)
    SERVICEABILITY_DENOMINATOR_CONSTANT: number; // 0.4
    SERVICEABILITY_DENOMINATOR_COEFFICIENT: number; // 1094

    // Subgrade Modulus Factor (Mr term)
    SUBGRADE_COEFFICIENT: number; // 2.32
    SUBGRADE_CONSTANT: number; // -8.07
}

/**
 * CBR (California Bearing Ratio) to Mr (Resilient Modulus) Conversion Constants
 * Based on AASHTO 1993 correlations
 */
export interface IAASHTOCBRConstants {
    // High CBR formula: Mr = 17.621 * CBR^0.64
    COEFFICIENT_HIGH_CBR: number; // 17.621
    EXPONENT_HIGH_CBR: number; // 0.64

    // Low CBR threshold and formula: Mr = CBR * 10 (for CBR <= 5)
    THRESHOLD_CBR: number; // 5
    MULTIPLIER_LOW_CBR: number; // 10

    // Unit conversion factors
    KPA_TO_PSI: number; // 0.145 (or 145 depending on formula context)
    KPA_MULTIPLIER: number; // 1000 (for MPa to kPa)
}

/**
 * Comprehensive unit conversion registry
 */
export interface IUnitConversions {
    // Length conversions
    LENGTH: {
        INCH_TO_METER: number;
        METER_TO_INCH: number;
        MM_TO_METER: number;
        METER_TO_MM: number;
        INCH_TO_MM: number;
        MM_TO_INCH: number;
    };

    // Pressure conversions
    PRESSURE: {
        KPA_TO_PSI: number;
        PSI_TO_KPA: number;
        KPA_TO_MPA: number;
        MPA_TO_KPA: number;
        PSI_TO_MPA: number;
        MPA_TO_PSI: number;
    };

    // Force conversions
    FORCE: {
        KN_TO_KIPS: number;
        KIPS_TO_KN: number;
        KN_TO_LBF: number;
        LBF_TO_KN: number;
    };
}

export interface IAASHTOCalculationSettings {
    // Goal Seek Configuration
    goalSeek: {
        tolerance: number;
        maxIterations: number;
        initialGuess: number;
        searchStep: number;
    };

    // Equation constants
    equation: IAASHTOEquationConstants;

    // CBR to Mr conversion constants
    cbr: IAASHTOCBRConstants;

    // Comprehensive unit conversion registry
    unitConversions: IUnitConversions;

    // Legacy: Kept for backward compatibility (deprecated - use unitConversions.LENGTH)
    conversions: {
        INCH_TO_METER: number;
        METER_TO_INCH: number;
    };
}

/**
 * AASHTO Constants Service - Centralized constant management
 */
export class AASHTOConstants {
    /**
     * Default AASHTO 1993 equation constants
     * These values are from the standard AASHTO methodology
     */
    private static readonly DEFAULT_CONSTANTS: IAASHTOEquationConstants = {
        // Structural Number term: 9.36*log(SN+1) - 0.2
        SN_COEFFICIENT: 9.36,
        SN_CONSTANT: -0.2,
        SN_EXPONENT: 5.19, // Used in serviceability denominator: (SN+1)^5.19

        // Serviceability term: log(ΔPSI/(4.2-1.5)) / (0.4 + 1094/(SN+1)^5.19)
        SERVICEABILITY_INTERCEPT: 4.2, // po_max in AASHTO
        SERVICEABILITY_SLOPE: 1.5, // pt_min in AASHTO
        SERVICEABILITY_DENOMINATOR_CONSTANT: 0.4,
        SERVICEABILITY_DENOMINATOR_COEFFICIENT: 1094,

        // Subgrade modulus term: 2.32*log(Mr) - 8.07
        SUBGRADE_COEFFICIENT: 2.32,
        SUBGRADE_CONSTANT: -8.07,
    };

    /**
     * Default CBR to Mr conversion constants (AASHTO 1993)
     */
    private static readonly DEFAULT_CBR_CONSTANTS: IAASHTOCBRConstants = {
        // High CBR formula: Mr(MPa) = 17.621 * CBR^0.64
        COEFFICIENT_HIGH_CBR: 17.621,
        EXPONENT_HIGH_CBR: 0.64,

        // Low CBR formula: Mr(MPa) = CBR * 10 (when CBR <= 5)
        THRESHOLD_CBR: 5,
        MULTIPLIER_LOW_CBR: 10,

        // Unit conversions
        KPA_TO_PSI: 0.145, // 1 kPa = 0.145 psi
        KPA_MULTIPLIER: 1000, // MPa to kPa: multiply by 1000
    };

    /**
     * Comprehensive unit conversion registry
     * All conversions are defined with exact precision where applicable
     */
    private static readonly DEFAULT_UNIT_CONVERSIONS: IUnitConversions = {
        LENGTH: {
            INCH_TO_METER: 0.0254, // Exact: 1 inch = 25.4 mm
            METER_TO_INCH: 39.3701, // 1 / 0.0254
            MM_TO_METER: 0.001, // Exact
            METER_TO_MM: 1000, // Exact
            INCH_TO_MM: 25.4, // Exact
            MM_TO_INCH: 0.0393701, // 1 / 25.4
        },
        PRESSURE: {
            KPA_TO_PSI: 0.145038, // 1 kPa = 0.145038 psi (precise)
            PSI_TO_KPA: 6.89476, // 1 psi = 6.89476 kPa
            KPA_TO_MPA: 0.001, // Exact: 1 MPa = 1000 kPa
            MPA_TO_KPA: 1000, // Exact
            PSI_TO_MPA: 0.00689476, // 1 psi = 0.00689476 MPa
            MPA_TO_PSI: 145.038, // 1 MPa = 145.038 psi
        },
        FORCE: {
            KN_TO_KIPS: 0.224809, // 1 kN = 0.224809 kip (precise)
            KIPS_TO_KN: 4.44822, // 1 kip = 4.44822 kN
            KN_TO_LBF: 224.809, // 1 kN = 224.809 lbf
            LBF_TO_KN: 0.00444822, // 1 lbf = 0.00444822 kN
        },
    };

    /**
     * Default calculation settings for AASHTO calculations
     */
    private static readonly DEFAULT_SETTINGS: IAASHTOCalculationSettings = {
        goalSeek: {
            tolerance: 1e-9,
            maxIterations: 200,
            initialGuess: 5.0,
            searchStep: 0.1,
        },
        equation: AASHTOConstants.DEFAULT_CONSTANTS,
        cbr: AASHTOConstants.DEFAULT_CBR_CONSTANTS,
        unitConversions: AASHTOConstants.DEFAULT_UNIT_CONVERSIONS,
        conversions: {
            // Legacy - kept for backward compatibility
            INCH_TO_METER: 0.0254,
            METER_TO_INCH: 39.3701,
        },
    };

    // Current active settings (can be overridden for testing or different standards)
    private static settings: IAASHTOCalculationSettings = { ...AASHTOConstants.DEFAULT_SETTINGS };

    /**
     * Get current AASHTO equation constants
     */
    public static getEquationConstants(): IAASHTOEquationConstants {
        return { ...this.settings.equation };
    }

    /**
     * Get current CBR conversion constants
     */
    public static getCBRConstants(): IAASHTOCBRConstants {
        return { ...this.settings.cbr };
    }

    /**
     * Get current unit conversion registry
     */
    public static getUnitConversions(): IUnitConversions {
        return { ...this.settings.unitConversions };
    }

    /**
     * Get current calculation settings
     */
    public static getCalculationSettings(): IAASHTOCalculationSettings {
        return { ...this.settings };
    }

    /**
     * Override equation constants (useful for testing or different standards)
     */
    public static setEquationConstants(constants: Partial<IAASHTOEquationConstants>): void {
        this.settings.equation = { ...this.settings.equation, ...constants };
    }

    /**
     * Override calculation settings
     */
    public static setCalculationSettings(settings: Partial<IAASHTOCalculationSettings>): void {
        this.settings = { ...this.settings, ...settings };
    }

    /**
     * Reset to default AASHTO 1993 constants
     */
    public static resetToDefaults(): void {
        this.settings = { ...AASHTOConstants.DEFAULT_SETTINGS };
    }

    /**
     * Validate that constants are within reasonable ranges
     */
    public static validateConstants(): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];
        const constants = this.settings.equation;

        // Validate SN coefficients
        if (constants.SN_COEFFICIENT <= 0) {
            errors.push('SN_COEFFICIENT must be positive');
        }
        if (constants.SN_EXPONENT <= 0) {
            errors.push('SN_EXPONENT must be positive');
        }

        // Validate serviceability constants
        if (constants.SERVICEABILITY_INTERCEPT <= constants.SERVICEABILITY_SLOPE) {
            errors.push('SERVICEABILITY_INTERCEPT must be greater than SERVICEABILITY_SLOPE');
        }
        if (constants.SERVICEABILITY_DENOMINATOR_CONSTANT <= 0) {
            errors.push('SERVICEABILITY_DENOMINATOR_CONSTANT must be positive');
        }
        if (constants.SERVICEABILITY_DENOMINATOR_COEFFICIENT <= 0) {
            errors.push('SERVICEABILITY_DENOMINATOR_COEFFICIENT must be positive');
        }

        // Validate subgrade constants
        if (constants.SUBGRADE_COEFFICIENT <= 0) {
            errors.push('SUBGRADE_COEFFICIENT must be positive');
        }

        return {
            isValid: errors.length === 0,
            errors,
        };
    }

    /**
     * Get constants formatted for debugging/logging
     */
    public static getDebugInfo(): Record<string, any> {
        return {
            constants: this.settings.equation,
            goalSeek: this.settings.goalSeek,
            conversions: this.settings.conversions,
            validation: this.validateConstants(),
        };
    }

    /**
     * Load constants from external configuration (future enhancement)
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public static loadFromConfig(config: any): void {
        // Future: Load from configuration service or file
        // For now, use defaults
        this.resetToDefaults();
    }
}

/**
 * Convenience function to get AASHTO equation constants
 */
export const getAASHTOConstants = (): IAASHTOEquationConstants => {
    return AASHTOConstants.getEquationConstants();
};

/**
 * Convenience function to get calculation settings
 */
export const getAASHTOSettings = (): IAASHTOCalculationSettings => {
    return AASHTOConstants.getCalculationSettings();
};
